import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import ChatPreview from "@/components/proactive-chats/chat-preview/chat-preview";
import { Button } from "@/components/ui/button";
import { useGetTrainingHistory } from "@/api/knowledge-source/get-training-history";
import { useEffect, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAddProactiveChatBulk } from "@/api/pro-active-chat/create-proactive-chat-bullk";
import useGlobalPermission from "@/store/user-permissions";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

type URLType = {
    id: string;
    url: string;
};

type ValuesType = {
    title: string;
    message: string;
    delay: number;
    scrollPercentage: number;
};

const AdvancedSetupSettings = () => {
    const [limit, setLimit] = useState(100);
    const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
    const [values, setValues] = useState({ title: "", message: "", delay: 0, scrollPercentage: 0 });
    const [errors, setErrors] = useState({ title: "", message: "", urls: "" });

    const { data: crawledUrl, isLoading, isError } = useGetTrainingHistory({ sourceType: "website", skip: 0, limit: limit });
    const { mutate } = useAddProactiveChatBulk();

    useEffect(() => {
        if (crawledUrl && !isLoading && !isError) {
            setLimit(crawledUrl?.pagination?.total_entity || 100);
        }
    }, [crawledUrl, isLoading, isError]);

    const addUrl = (url: string) => {
        setSelectedUrls((prev) => [...prev, url]);
    };

    const removeUrl = (url: string) => {
        setSelectedUrls((prev) => prev.filter((item) => item !== url));
    };

    const toggleSelectAll = () => {
        if (crawledUrl?.pagination?.total_entity === selectedUrls?.length) {
            setSelectedUrls([]);
            return;
        }
        setSelectedUrls(crawledUrl?.data?.map((url: URLType) => url?.url));
    };

    const valueChange = (key: string, value: string | number) => {
        const tempValue: ValuesType = { ...values };
        if (key === "title" || key === "message") {
            tempValue[key] = value as string;
        } else if (key === "delay" || key === "scrollPercentage") {
            tempValue[key] = value as number;
        }
        setValues(tempValue);
    };

    const handleSave = () => {
        if (!values?.title || !values?.message || selectedUrls?.length === 0) {
            setErrors({
                title: values?.title ? "" : "Title is required",
                message: values?.message ? "" : "Message is required",
                urls: selectedUrls?.length === 0 ? "Select at least one url" : "",
            });
            return;
        }
        setErrors({ title: "", message: "", urls: "" });
        mutate(
            selectedUrls?.map((url) => ({
                title: values?.title,
                message: values?.message,
                url: url,
                is_active: true,
                delay: values?.delay,
                scrawl_percentage: values?.scrollPercentage,
            }))
        );
    };

    const { permissionList } = useGlobalPermission();
    const proactivePermission = permissionList.find((item) => item.module.name === "Proactive_Chat");

    return (
        <div className="relative">
            {!proactivePermission?.can_create && (
                <Alert variant="destructive" className="absolute z-10 top-1/4 right-1/2 translate-x-1/2 max-w-2xl">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>You do not have permission to view this list!</AlertDescription>
                </Alert>
            )}
            <div className={cn(!proactivePermission?.can_create && "blur-md")}>
                <div className={cn("flex flex-col items-center justify-between md:flex-row")}>
                    <h3 className="mb-4 text-xl font-bold text-gray-700 md:mb-0">To Start, Decide Where You&apos;d Like this to be Displayed.</h3>
                    {/* <Button className="w-full px-10 py-5 text-lg font-normal md:w-auto">Select by URL</Button> */}
                </div>
                <Button disabled={!proactivePermission?.can_create} className="mt-4" size={"sm"} onClick={toggleSelectAll}>
                    {crawledUrl?.pagination?.total_entity === selectedUrls?.length ? "Unselect All" : "Select All"}
                </Button>
                <div className="max-h-64 overflow-y-auto mt-4">
                    {isLoading && [1, 2, 3, 4, 5, 7, 8, 9, 10].map((item) => <Skeleton key={item} className="h-4 w-96 my-4" />)}
                    {!isLoading &&
                        !isError &&
                        crawledUrl?.data?.map((url: URLType) => (
                            <div className="flex items-center gap-2 my-3" key={url?.id}>
                                <Checkbox
                                    disabled={!proactivePermission?.can_create}
                                    id={url?.id}
                                    checked={selectedUrls.includes(url?.url)}
                                    onCheckedChange={(value) => {
                                        if (value) {
                                            addUrl(url?.url);
                                        } else {
                                            removeUrl(url?.url);
                                        }
                                    }}
                                />
                                <label
                                    htmlFor={url?.id}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                    {proactivePermission?.can_create ? url?.url : "No permission to view"}
                                </label>
                            </div>
                        ))}
                </div>
                {errors?.urls && <p className="mt-1 text-sm text-red-500">{errors?.urls}</p>}

                <Separator className="my-6" />
                <h3 className="text-xl font-bold text-gray-700">To Start, Decide Where You&apos;d Like this to be Displayed.</h3>
                <div className="mt-2 flex items-center gap-2">
                    <p className="font-semibold">Show</p>
                    <Input
                        disabled={!proactivePermission?.can_create}
                        className="max-w-16"
                        value={values?.delay}
                        onChange={(e) => valueChange("delay", e.target.value)}
                        type="number"
                    />

                    <p className="font-semibold">Seconds After Page Load</p>
                </div>
                <p className="mt-1 text-sm">As soon as a visitor lands on any page, the timer will start and they will receive this notification</p>
                <div className="mt-4 flex items-center gap-2">
                    <p className="font-semibold">Show After Scrolling</p>
                    <div className="flex items-center gap-1">
                        <Input
                            disabled={!proactivePermission?.can_create}
                            className="max-w-16"
                            value={values?.scrollPercentage}
                            onChange={(e) => valueChange("scrollPercentage", e.target.value)}
                            type="number"
                        />{" "}
                        %
                    </div>
                    <p className="font-semibold">of the Page</p>
                </div>
                <p className="mt-1 text-sm">
                    As soon as a visitor lands on this page and scroll at least xx% of the page, they will receive this notification
                </p>

                <Separator className="my-6" />

                <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className={cn("order-2 border-t pr-6 pt-4 lg:order-1 lg:border-r lg:border-t-0 lg:pt-0")}>
                        <div>
                            <h3 className="text-xl font-bold text-gray-700">Write Your Message</h3>
                            <p className="mb-4 text-sm text-gray-700">
                                This message will appear as a little &quot;popup&quot; above the chat bubble.
                            </p>
                            <Input
                                disabled={!proactivePermission?.can_create}
                                placeholder="Step & Feet"
                                className="mt-2 h-12 p-4 text-xl"
                                value={values?.title}
                                onChange={(e) => valueChange("title", e.target.value)}
                            />
                            {errors?.title && <p className="mt-1 text-sm text-red-500">{errors?.title}</p>}
                            <p className="my-4 text-sm text-gray-700">This message will be in the conversation for the user to see.</p>
                            <Textarea
                                disabled={!proactivePermission?.can_create}
                                placeholder="Hello, I'm Shoe AI Chatbot and I'm here to help you with your queries. How can I help you today?"
                                className="mt-2 h-20 p-4 text-xl"
                                value={values?.message}
                                onChange={(e) => valueChange("message", e.target.value)}
                            />
                            {errors?.message && <p className="mt-1 text-sm text-red-500">{errors?.message}</p>}
                        </div>
                    </div>
                    <ChatPreview
                        className={"order-1 h-full pb-16 lg:order-2 lg:pb-8 lg:pl-6"}
                        title={values?.title || "Step & Feet"}
                        message={values?.message || "Hi, I'm Shoe AI Chatbot and I'm here to help you with your queries. How can I help you today?"}
                    />
                </div>
                <Separator className="my-6" />
                <div className="md:max-w-[50%]">
                    <div className="mt-8 grid grid-cols-2 gap-4">
                        <Button disabled={!proactivePermission?.can_create} variant={"outline"} className="py-5 text-xl text-gray-600">
                            Cancel
                        </Button>
                        <Button className="py-5 text-xl" onClick={handleSave} disabled={!proactivePermission?.can_create}>
                            Save
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdvancedSetupSettings;
